/* Collection images only: no product data writes, timers or automatic navigation. */
if (!customElements.get('oe-card-gallery')) {
  customElements.define('oe-card-gallery', class extends HTMLElement {
    connectedCallback() {
      this.image = this.querySelector('.oe-collection-card__image img');
      this.count = Number(this.dataset.imageCount);
      if (!this.image || this.count < 2 || !this.querySelector('[data-card-images]')) return;
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.index ??= Number(this.dataset.initialIndex) || 0;
      this.targetIndex = this.index;
      this.dots = Array.from(this.querySelectorAll('[data-card-image-dot]'));
      this.loads ??= new Map();
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-card-image-step]');
        if (button || this.suppressClick) {
          event.preventDefault();
          event.stopPropagation();
          this.suppressClick = false;
          if (button && !button.disabled) this.step(Number(button.dataset.cardImageStep));
        }
      }, { ...options, capture: true });
      // iOS can cancel the pointer stream while retaining the touch sequence.
      // Handle that sequence directly; do not also process its pointer events.
      const touchEvents = 'ontouchstart' in window;
      this.addEventListener('touchstart', (event) => {
        this.suppressClick = false;
        const touch = event.touches.length === 1 ? event.touches[0] : null;
        this.gesture = touch ? { kind: 'touch', id: touch.identifier, x: touch.clientX, y: touch.clientY } : null;
      }, { ...options, capture: true, passive: true });
      this.addEventListener('touchmove', (event) => {
        const g = this.gesture;
        if (!g || g.kind !== 'touch') return;
        if (event.touches.length !== 1) { this.gesture = null; return; }
        const touch = Array.from(event.touches).find(t => t.identifier === g.id);
        if (touch) this.moveGesture(touch.clientX, touch.clientY, event);
      }, { ...options, capture: true, passive: false });
      this.addEventListener('touchend', (event) => {
        const g = this.gesture;
        if (!g || g.kind !== 'touch') return;
        const touch = Array.from(event.changedTouches).find(t => t.identifier === g.id);
        if (touch) this.endGesture(touch.clientX, touch.clientY, event);
      }, { ...options, capture: true, passive: false });
      this.addEventListener('touchcancel', () => { this.gesture = null; }, options);
      this.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch' && touchEvents) return;
        this.suppressClick = false;
        if (event.pointerType === 'mouse' || !event.isPrimary) { this.gesture = null; return; }
        this.gesture = { kind: 'pointer', id: event.pointerId, x: event.clientX, y: event.clientY };
      }, options);
      this.addEventListener('pointermove', (event) => {
        const g = this.gesture;
        if (!g || g.kind !== 'pointer' || g.id !== event.pointerId) return;
        this.moveGesture(event.clientX, event.clientY, event);
        if (g.axis === 'horizontal') {
          if (!this.hasPointerCapture(event.pointerId)) this.setPointerCapture(event.pointerId);
        }
      }, options);
      this.addEventListener('pointerup', (event) => {
        const g = this.gesture;
        if (g?.kind === 'pointer' && g.id === event.pointerId) this.endGesture(event.clientX, event.clientY, event);
      }, options);
      this.addEventListener('pointercancel', () => {
        if (this.gesture?.kind === 'pointer') this.gesture = null;
      }, options);
      this.updateButtons();
      this.updateDots();
      this.dataset.galleryReady = '';
    }

    moveGesture(x, y, event) {
      const g = this.gesture, dx = Math.abs(x - g.x), dy = Math.abs(y - g.y);
      if (!g.axis) {
        if (dy >= 10 && dy >= dx) g.axis = 'vertical';
        else if (dx >= 12 && dx > dy * 1.5) g.axis = 'horizontal';
      }
      if (g.axis === 'horizontal' && event.cancelable) event.preventDefault();
    }

    endGesture(x, y, event) {
      const g = this.gesture;
      this.gesture = null;
      if (g.axis !== 'horizontal') return;
      this.suppressClick = true;
      if (event.cancelable) event.preventDefault();
      const dx = x - g.x, dy = y - g.y;
      if (Math.abs(dx) >= 28 && Math.abs(dx) > Math.abs(dy) * 1.5) this.step(dx < 0 ? 1 : -1);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.version = (this.version || 0) + 1;
      this.gesture = null;
    }

    updateButtons() {
      this.querySelector('[data-card-image-step="-1"]').disabled = this.targetIndex <= 0;
      this.querySelector('[data-card-image-step="1"]').disabled = this.targetIndex >= this.count - 1;
    }

    updateDots() {
      // Keep every image in order, showing a compact window around the visible image.
      const start = Math.max(0, Math.min(this.index - 1, this.count - 3));
      this.dots.forEach((dot, index) => {
        dot.hidden = index < start || index >= start + 3;
        dot.classList.toggle('is-active', index === this.index);
      });
    }

    async step(delta) {
      const next = Math.max(0, Math.min(this.count - 1, this.targetIndex + delta));
      if (next === this.targetIndex) return;
      this.targetIndex = next;
      this.updateButtons();
      const version = this.version = (this.version || 0) + 1;
      try {
        this.images ??= JSON.parse(this.querySelector('[data-card-images]').textContent);
        const data = this.images[next];
        if (!this.loads.has(next)) {
          const pending = new Image();
          const widths = [...new Set([360, 540, 720, 1080].map(w => Math.min(w, data.width)))];
          pending.sizes = this.image.sizes;
          pending.srcset = widths.map(width => {
            const url = new URL(data.src, location.href);
            url.searchParams.set('width', String(width));
            return `${url.href} ${width}w`;
          }).join(', ');
          pending.src = data.src;
          this.loads.set(next, pending.decode().then(() => pending).catch(error => {
            this.loads.delete(next);
            throw error;
          }));
        }
        const loaded = await this.loads.get(next);
        if (!this.isConnected || version !== this.version) return;
        this.image.srcset = loaded.srcset;
        this.image.src = loaded.src;
        this.image.alt = data.alt;
        this.image.width = data.width;
        this.image.height = data.height;
        this.index = next;
        this.updateDots();
      } catch {
        // Keep the working image and allow an explicit retry after a failed request.
        if (version === this.version) this.targetIndex = this.index;
      } finally {
        if (this.isConnected && version === this.version) this.updateButtons();
      }
    }
  });
}
